// src/pages/VendaPage.tsx
import React, { useEffect, useState, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import { api } from "../services/api";
import Swal from "sweetalert2";
import { useLocation } from "react-router-dom";
import { AxiosError } from "axios";

/* Tipagens */
interface CategoriaProduto {
  id: number;
  nome: string;
  descricao?: string;
  ativo?: boolean;
}

interface Produto {
  id: number;
  nome: string;
  precoVenda: number;
  imagemUrl?: string | null;
  ativo?: boolean;
  quantidadeEstoque: number;
  categoriaProdutoId: number;
  categoriaProduto?: CategoriaProduto | null;
}

interface FormaPagamento {
  id: number;
  nome: string;
}

interface CartItem {
  produtoId: number;
  nome: string;
  precoUnitario: number;
  quantidade: number;
}

interface Evento {
  id: number;
  descricao: string;
  valorInicialCaixa: number;
  totalReceita: number;
  ativo: boolean;
  descontoEquipePercentual?: number | null;
}

interface Equipe {
  id: number;
  nome: string;
}

const VendaPage: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const eventoIdParam = queryParams.get("eventoId");

  const [eventoAtivo, setEventoAtivo] = useState<Evento | null>(null);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [formasPagamento, setFormasPagamento] = useState<FormaPagamento[]>([]);
  const [equipes, setEquipes] = useState<Equipe[]>([]);
  const [EquipeId, setEquipeId] = useState<number | null>(null);

  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedFormaPagamento, setSelectedFormaPagamento] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const [isEquipe, setIsEquipe] = useState(false);
  const [isFiado, setIsFiado] = useState(false);
  const [nomeFiado, setNomeFiado] = useState("");

  /* Loaders */
  const loadEquipes = useCallback(async () => {
    try {
      const res = await api.get<Equipe[]>("/equipes");
      const equipesAtivas = (res.data || []).filter((e: any) => e.ativo !== false);
      setEquipes(equipesAtivas);
    } catch {
      Swal.fire("Erro", "Falha ao carregar equipes.", "error");
    }
  }, []);

  const carregarEventoAtivo = useCallback(async () => {
    try {
      if (eventoIdParam) {
        try {
          const resp = await api.get<Evento>(`/evento/${eventoIdParam}`);
          setEventoAtivo(resp.data);
          return;
        } catch {
          Swal.fire("Aviso", "Evento informado não encontrado. Carregando evento ativo.", "warning");
        }
      }
      const { data } = await api.get<Evento[]>("/evento");
      setEventoAtivo(data.find((e) => e.ativo) || null);
    } catch {
      Swal.fire("Erro", "Falha ao carregar evento.", "error");
    }
  }, [eventoIdParam]);

  const loadProdutos = useCallback(async () => {
    try {
      const res = await api.get<Produto[]>("/produto");
      setProdutos(
        (res.data || [])
          .filter((p) => p.ativo !== false)
          .map((p) => ({
            ...p,
            quantidadeEstoque: typeof p.quantidadeEstoque === "number" ? p.quantidadeEstoque : 0,
            categoriaProdutoId: (p as any).categoriaProdutoId ?? (p.categoriaProduto?.id ?? 0),
          }))
      );
    } catch {
      Swal.fire("Erro", "Não foi possível carregar os produtos.", "error");
    }
  }, []);

  const loadFormasPagamento = useCallback(async () => {
    try {
      const res = await api.get<FormaPagamento[]>("/formaPagamento");
      setFormasPagamento(res.data || []);
      if ((res.data || []).length > 0) setSelectedFormaPagamento(res.data[0].id);
    } catch {
      Swal.fire("Erro", "Falha ao carregar formas de pagamento.", "error");
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        loadProdutos(),
        loadFormasPagamento(),
        carregarEventoAtivo(),
        loadEquipes()
      ]);
      setLoading(false);
    };
    init();
  }, [loadProdutos, loadFormasPagamento, carregarEventoAtivo, loadEquipes]);

  /* Carrinho */
  const addToCart = (p: Produto) => {
    if (p.quantidadeEstoque <= 0) {
      Swal.fire("Estoque insuficiente", "Este produto está sem estoque.", "warning");
      return;
    }

    setCart((prev) => {
      const item = prev.find((i) => i.produtoId === p.id);
      if (item)
        return prev.map((i) =>
          i.produtoId === p.id ? { ...i, quantidade: i.quantidade + 1 } : i
        );

      return [...prev, { produtoId: p.id, nome: p.nome, precoUnitario: p.precoVenda, quantidade: 1 }];
    });
  };

  const incQty = (id: number) =>
    setCart((prev) =>
      prev.map((i) => (i.produtoId === id ? { ...i, quantidade: i.quantidade + 1 } : i))
    );

  const decQty = (id: number) =>
    setCart((prev) =>
      prev
        .map((i) => (i.produtoId === id ? { ...i, quantidade: Math.max(0, i.quantidade - 1) } : i))
        .filter((i) => i.quantidade > 0)
    );

  const removeItem = (id: number) =>
    setCart((prev) => prev.filter((i) => i.produtoId !== id));

  const clearCart = () => {
    setCart([]);
    setIsEquipe(false);
    setIsFiado(false);
    setNomeFiado("");
    setEquipeId(null);
  };

  const totalVendido = cart.reduce((acc, item) => {
    const desconto = isEquipe && eventoAtivo?.descontoEquipePercentual
      ? eventoAtivo.descontoEquipePercentual
      : 0;

    const precoComDesconto = item.precoUnitario * (1 - (desconto ?? 0) / 100);
    return acc + precoComDesconto * item.quantidade;
  }, 0);

  const formatCurrency = (v: number) =>
    v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  /* FINALIZAR VENDA */
  const handleFinalizarVenda = async () => {
    if (cart.length === 0)
      return Swal.fire("Atenção", "Carrinho vazio.", "warning");

    if (!eventoAtivo)
      return Swal.fire("Erro", "Nenhum evento ativo encontrado.", "error");

    if (isEquipe && !EquipeId)
      return Swal.fire("Atenção", "Selecione uma equipe.", "warning");

    if (isFiado && !isEquipe)
      return Swal.fire("Atenção", "Venda fiado só é permitida para equipe.", "warning");

    if (isFiado && nomeFiado.trim().length < 2)
      return Swal.fire("Atenção", "Digite o nome do comprador.", "warning");

    let formaPagamentoFinal = selectedFormaPagamento;
    let statusFinal = 1; // Pago

    if (isFiado) {
      formaPagamentoFinal = 5; // FIADO SEMPRE USA ID 5
      statusFinal = 2;         // FIADO = PENDENTE
    }

    try {
      const res = await api.post("/vendas", {
        eventoId: eventoAtivo.id,
        formaPagamentoId: formaPagamentoFinal,
        statusId: statusFinal,
        isEquipe,
        isFiado,
        equipeId: isEquipe ? EquipeId : null,
        nomeCompradorFiado: isFiado ? nomeFiado.trim() : null,
        itens: cart.map((i) => ({
          produtoId: i.produtoId,
          quantidade: i.quantidade
        }))
      });

      const vendaId = (res.data && (res.data.id ?? res.data)) ?? null;

      Swal.fire("Sucesso", "Venda registrada!", "success");

      /* IMPRESSÃO VIA API */
      try {
        await api.post("/Vendas/imprimirFichas", {
          vendaId,
          eventoNome: eventoAtivo?.descricao ?? "",
          itens: cart.map((i) => ({
            nome: i.nome,
            quantidade: i.quantidade
          }))
        });

        Swal.fire("Impressão", "Fichas enviadas para a impressora!", "success");
      } catch {
        Swal.fire("Aviso", "Venda realizada, mas falhou ao imprimir.", "warning");
      }

      clearCart();
      await loadProdutos();
      await carregarEventoAtivo();

    } catch (err: any) {
      console.error("Erro criar venda:", err);

      const axiosError = err as AxiosError;
      const msg = axiosError.response?.data ?? null;

      Swal.fire("Erro", msg ? String(msg) : "Falha ao registrar venda.", "error");
    }
  };

  /* Produtos agrupados */
  const produtosPorCategoria = React.useMemo(() => {
    const map =
      new Map<number, { categoria: CategoriaProduto | null; produtos: Produto[] }>();

    for (const p of produtos) {
      const catId = p.categoriaProdutoId ?? p.categoriaProduto?.id ?? 0;

      if (!map.has(catId)) {
        map.set(catId, { categoria: p.categoriaProduto ?? null, produtos: [] });
      }

      map.get(catId)!.produtos.push(p);
    }

    return Array.from(map.values()).map((v) => ({
      categoria: v.categoria,
      produtos: v.produtos,
    }));
  }, [produtos]);

  /* Renderização */
  if (loading) {
    return (
      <div className="d-flex">
        <Sidebar />
        <div className="flex-grow-1 p-4 text-center">
          <div className="spinner-border text-success" style={{ width: 60, height: 60 }} />
          <p className="mt-3 text-muted">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!eventoAtivo) {
    return (
      <div className="d-flex">
        <Sidebar />
        <div className="flex-grow-1 p-4">
          <div className="alert alert-warning">Nenhum evento ativo.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex">
      <Sidebar />

      <div className="flex-grow-1 p-4" style={{ marginLeft: 220 }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>💰 Vendas — {eventoAtivo.descricao}</h2>

          {/*<div>
            <span className="badge bg-primary">Impressora (COM3)</span>
          </div>*/}
        </div>

        <div className="row g-4">
          {/* --------- ESQUERDA: PRODUTOS --------- */}
          <div className="col-md-8">
            {produtosPorCategoria.map((group, idx) => {
              const catName = group.categoria?.nome ?? "Outros";
              const catDesc = group.categoria?.descricao ?? "";

              return (
                <div key={idx} className="mb-4">
                  <div className="d-flex align-items-baseline justify-content-between mb-2">
                    <div>
                      <h5 className="mb-0">{catName}</h5>
                      {catDesc && <small className="text-muted ms-2">{catDesc}</small>}
                    </div>
                    <div className="text-muted small">Todos os itens</div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      overflowX: "auto",
                      paddingBottom: 8,
                      paddingTop: 8,
                    }}
                  >
                    {group.produtos.map((p) => {
                      const semEstoque = p.quantidadeEstoque <= 0;
                      const inCart = cart.find((c) => c.produtoId === p.id);
                      const estoqueColor =
                        semEstoque ? "bg-dark" :
                          p.quantidadeEstoque < 10 ? "bg-danger" : "bg-secondary";

                      return (
                        <div key={p.id} style={{ minWidth: 140, maxWidth: 140 }}>
                          <div
                            className="card shadow-sm h-100 position-relative"
                            style={{
                              cursor: semEstoque ? "not-allowed" : "pointer",
                              borderRadius: 12,
                              opacity: semEstoque ? 0.5 : 1,
                            }}
                            onClick={() => !semEstoque && addToCart(p)}
                          >
                            {/* Estoque */}
                            <span
                              className={`badge ${estoqueColor}`}
                              style={{
                                position: "absolute",
                                top: 6,
                                left: 6,
                                padding: "4px 7px",
                                fontSize: 12,
                              }}
                            >
                              {semEstoque ? "Esgotado" : p.quantidadeEstoque}
                            </span>

                            {/* Quantidade no carrinho */}
                            {inCart && !semEstoque && (
                              <span
                                className="badge bg-success"
                                style={{
                                  position: "absolute",
                                  top: 6,
                                  right: 6,
                                  padding: "4px 7px",
                                  fontSize: 12,
                                }}
                              >
                                {inCart.quantidade}
                              </span>
                            )}

                            {/* Imagem */}
                            <div
                              style={{
                                height: 70,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                background: "#f8f8f8",
                                borderTopLeftRadius: 12,
                                borderTopRightRadius: 12,
                              }}
                            >
                              <img
                                src={p.imagemUrl ?? "/placeholder-food.png"}
                                alt={p.nome}
                                style={{ maxHeight: 60, objectFit: "contain", width: "80%" }}
                              />
                            </div>

                            {/* Info */}
                            <div className="card-body p-2">
                              <strong style={{ fontSize: 13 }}>{p.nome}</strong>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                {formatCurrency(p.precoVenda)}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <hr />
                </div>
              );
            })}
          </div>

          {/* --------- DIREITA: CARRINHO --------- */}
          <div className="col-md-4">
            <div className="card shadow-sm p-3">
              <h5 className="mb-3">Carrinho</h5>

              <div style={{ maxHeight: 350, overflowY: "auto" }}>
                {cart.length === 0 && <p className="text-muted">Nenhum item adicionado.</p>}

                {cart.map((item) => {
                  const desconto = isEquipe && eventoAtivo?.descontoEquipePercentual
                    ? eventoAtivo.descontoEquipePercentual
                    : 0;

                  const precoComDesconto =
                    item.precoUnitario * (1 - (desconto ?? 0) / 100);

                  return (
                    <div
                      className="d-flex justify-content-between align-items-center mb-2"
                      key={item.produtoId}
                    >
                      <div>
                        <strong>{item.nome}</strong>

                        <div className="text-muted" style={{ fontSize: 13 }}>
                          {desconto > 0 ? (
                            <>
                              <span style={{ textDecoration: "line-through", marginRight: 6 }}>
                                {formatCurrency(item.precoUnitario)}
                              </span>
                              <span>{formatCurrency(precoComDesconto)}</span>
                            </>
                          ) : (
                            <span>{formatCurrency(item.precoUnitario)}</span>
                          )}
                        </div>
                      </div>

                      <div className="d-flex align-items-center">
                        {/* - */}
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => decQty(item.produtoId)}
                        >
                          −
                        </button>

                        <div style={{ width: 30, textAlign: "center" }}>{item.quantidade}</div>

                        {/* + */}
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => incQty(item.produtoId)}
                        >
                          +
                        </button>

                        <span className="ms-3" style={{ width: 75, textAlign: "right" }}>
                          {formatCurrency(precoComDesconto * item.quantidade)}
                        </span>

                        <button
                          className="btn btn-sm btn-outline-danger ms-2"
                          onClick={() => removeItem(item.produtoId)}
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <hr />

              {/* -------- OPÇÕES DA VENDA -------- */}
              <div className="mb-3 p-3 border rounded" style={{ background: "#fafafa" }}>
                <h6 className="mb-2">Opções da Venda</h6>

                {/* Venda de equipe */}
                <div className="form-check mb-1">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="chkEquipe"
                    checked={isEquipe}
                    onChange={(e) => {
                      setIsEquipe(e.target.checked);
                      if (!e.target.checked) {
                        setIsFiado(false);
                        setNomeFiado("");
                        setEquipeId(null);
                      }
                    }}
                  />
                  <label className="form-check-label" htmlFor="chkEquipe">
                    Venda de equipe
                  </label>
                </div>

                {/* Seleção de equipe */}
                {isEquipe && (
                  <div className="mt-2 ms-3">
                    <label className="form-label small mb-1">Selecione a equipe</label>
                    <select
                      className="form-select"
                      value={EquipeId ?? ""}
                      onChange={(e) => setEquipeId(Number(e.target.value))}
                    >
                      <option value="">Selecione...</option>
                      {equipes.map((eq) => (
                        <option key={eq.id} value={eq.id}>
                          {eq.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Fiado */}
                {isEquipe && (
                  <div className="ms-3 mt-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="chkFiado"
                        checked={isFiado}
                        onChange={(e) => {
                          setIsFiado(e.target.checked);
                          if (!e.target.checked) setNomeFiado("");
                        }}
                      />
                      <label className="form-check-label" htmlFor="chkFiado">
                        Venda fiado (somente equipe)
                      </label>
                    </div>

                    {isFiado && (
                      <div className="mt-2">
                        <label className="form-label small mb-1">Nome do comprador</label>
                        <input
                          type="text"
                          className="form-control"
                          value={nomeFiado}
                          onChange={(e) => setNomeFiado(e.target.value)}
                          placeholder="Digite o nome de quem está comprando"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* -------- TOTAL -------- */}
              <div className="d-flex justify-content-between mb-3">
                <strong>Total</strong>
                <strong style={{ fontSize: 18 }}>{formatCurrency(totalVendido)}</strong>
              </div>

              {/* -------- FORMAS DE PAGAMENTO -------- */}
              <h6>Forma de Pagamento</h6>

              {formasPagamento.map((f) => (
                <div className="form-check" key={f.id}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="fp"
                    id={`fp-${f.id}`}
                    disabled={isFiado}
                    checked={isFiado ? f.id === 5 : selectedFormaPagamento === f.id}
                    onChange={() => !isFiado && setSelectedFormaPagamento(f.id)}
                  />
                  <label className="form-check-label" htmlFor={`fp-${f.id}`}>
                    {f.nome}
                  </label>
                </div>
              ))}

              {/* -------- BOTÕES -------- */}
              <div className="d-grid gap-2 mt-3">
                <button
                  className="btn btn-success"
                  disabled={cart.length === 0}
                  onClick={handleFinalizarVenda}
                >
                  Finalizar Venda
                </button>

                <button className="btn btn-outline-secondary" onClick={clearCart}>
                  Limpar Carrinho
                </button>
              </div>
            </div>

            <p className="text-muted mt-3 small">
              • Clique no produto para adicionar <br />
              • Cada unidade gera 1 ficha <br />
              • Impressora configurada localmente (COM3)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendaPage;
